import { defineStyles } from '../hooks/defineStyles';
import { postBodyStyles, smallPostStyles, commentBodyStyles } from '../../themes/stylePiping'
import classNames from 'classnames';
import { researchAccentTint } from '../research/researchStyleUtils';

/**
 * Research-document editor styling. Inherits the full postBodyStyles surface
 * (so spoilers, footnotes, embeds, tables, code blocks, etc. all look right),
 * then restyles the editor's own typography under `[contenteditable="true"]`:
 * a serif essay reading column (research docs read as essays-in-progress, not
 * tool output) for paragraphs / headings / lists / blockquotes, plus
 * placeholder positioning that lines up with where the user's first paragraph
 * would actually sit.
 *
 * Keep editor-specific rules scoped to the contenteditable so they don't leak
 * onto floating menus, toolbars, or popovers that share the wrapper. The
 * `:not(.research-query-input-content)`, `:not(.research-chat-composer *)`,
 * `:not(.research-agent-block *)`, and `:not(:where(.LexicalContentEditable-rootComment))`
 * guards exclude the query input, the nested conversation-block composer,
 * agent transcript/presentation content, and the comment composers (margin
 * cards portal inside this wrapper), which carry their own compact styling
 * rather than the document's reading column.
 *
 * The comment-composer guards are wrapped in `:where()` because these
 * selectors tie with DocumentPane's `editorWrapWithComments` margin override
 * at equal specificity and must keep losing that tie by source order — a
 * bare `:not(.x)` raises specificity and silently re-centers the column
 * while comment threads are open.
 */
export const RESEARCH_DOC_EDITOR_MAX_WIDTH = 760 + (2 * 32);

const researchDocumentBodyStyles = (theme: ThemeType) => ({
  ...postBodyStyles(theme),
  '& [contenteditable="true"]:not(.research-query-input-content):not(.research-chat-composer *):not(:where(.LexicalContentEditable-rootComment))': {
    minHeight: 'calc(100vh - var(--header-height, 0px))',
    boxSizing: 'border-box',
    fontSize: 18,
    lineHeight: 1.65,
    fontFamily: theme.palette.fonts.serifStack,
    color: theme.palette.text.primary,
    maxWidth: RESEARCH_DOC_EDITOR_MAX_WIDTH,
    margin: '0 auto',
    padding: '44px 32px 160px',
  },
  // Collapsible sections share the paragraph rule so they can't drift from it.
  '& [contenteditable="true"] p:not(.research-agent-block *):not(:where(.LexicalContentEditable-rootComment *)), & [contenteditable="true"] .detailsBlock:not(.research-agent-block *)': {
    margin: '0 0 0.7em',
  },
  '& [contenteditable="true"] h1:not(.research-agent-block *)': {
    fontSize: 32,
    lineHeight: 1.2,
    margin: '0.9em 0 0.45em',
    fontWeight: 400,
    fontFamily: theme.palette.fonts.headerStack,
  },
  '& [contenteditable="true"] h2:not(.research-agent-block *)': {
    fontSize: 24,
    lineHeight: 1.25,
    margin: '1em 0 0.45em',
    fontWeight: 600,
    fontFamily: theme.palette.fonts.serifStack,
  },
  '& [contenteditable="true"] h3:not(.research-agent-block *)': {
    fontSize: 20,
    lineHeight: 1.3,
    margin: '1em 0 0.45em',
    fontWeight: 600,
    fontFamily: theme.palette.fonts.serifStack,
  },
  '& [contenteditable="true"] h4:not(.research-agent-block *)': {
    fontSize: 18,
    lineHeight: 1.35,
    margin: '1em 0 0.45em',
    fontWeight: 600,
    fontStyle: 'italic',
    fontFamily: theme.palette.fonts.serifStack,
  },
  '& [contenteditable="true"] li:not(.research-agent-block *)': {
    fontSize: 18,
    lineHeight: 1.65,
    fontFamily: theme.palette.fonts.serifStack,
    color: theme.palette.text.primary,
  },
  '& [contenteditable="true"] blockquote:not(.research-agent-block *)': {
    fontSize: 18,
    lineHeight: 1.65,
    fontFamily: theme.palette.fonts.serifStack,
    margin: '0.5em 0',
    padding: '0.25em 0.75em',
    borderLeft: `3px solid ${theme.palette.greyAlpha(0.15)}`,
    color: theme.palette.text.primary,
    fontStyle: 'normal',
  },
  '& mark.editor-mark': {
    background: researchAccentTint(0.14),
    borderBottom: `2px solid ${researchAccentTint(0.45)}`,
    padding: '1px 0',
    color: 'inherit',
  },
  '& mark.editor-mark.selected': {
    background: researchAccentTint(0.28),
    borderBottom: `2px solid ${researchAccentTint(0.7)}`,
  },
  // Placeholder is a sibling of the contenteditable, absolutely positioned
  // at the top-left of the editor shell, so it doesn't pick up the
  '& .LexicalContentEditable-placeholder:not(.research-chat-composer *):not(:where(.LexicalContentEditable-placeholderComment))': {
    top: 44,
    left: 0,
    right: 0,
    maxWidth: 760,
    margin: '0 auto',
    fontSize: 18,
    lineHeight: 1.65,
    fontStyle: 'italic',
    fontFamily: theme.palette.fonts.serifStack,
  },
});

export const styles = defineStyles("ContentStyles", (theme: ThemeType) => ({
  base: {
    ...postBodyStyles(theme)
  },
  postBody: {
  },
  postHighlight: {
    ...smallPostStyles(theme),
    '& h1, & h2, & h3': {
      fontSize: "1.6rem",
      // Cancel out a negative margin which would cause clipping
      marginBlockStart: "0 !important",
    },
  },
  commentBody: {
    ...commentBodyStyles(theme),
  },
  commentBodyExceptPointerEvents: {
    ...commentBodyStyles(theme, true)
  },
  debateResponseBody: {
    ...commentBodyStyles(theme),
    fontSize: '1.35rem',
    '& .dialogue-message-header + p': {
      marginTop: 0,
    },
    '& blockquote, & li': {
      fontSize: '1.35rem'
    }
  },
  answerBody: {
    ...smallPostStyles(theme)
  },
  tagBody: {
    ...commentBodyStyles(theme),
    marginBottom: 18,
    '&& h1': {
      fontSize: '2rem',
      marginTop: '3rem',
      fontWeight:600,
      ...theme.typography.commentStyle
    },
    '&& h2': {
      fontSize: '1.7rem',
      marginTop: '1.5rem',
      fontWeight:500,
      ...theme.typography.commentStyle
    },
    '&& h3': {
      fontSize: '1.3rem',
      marginTop: '1.5rem',
      fontWeight:500,
      ...theme.typography.commentStyle
    },
    '&& h1:first-child, h2:first-child, h3:first-child': {
      marginTop: 0,
    },
  },
  llmChat: {
    ...commentBodyStyles(theme),
    fontSize: '1.0rem',
    '& blockquote, & li': {
      fontSize: '1.0rem'
    }
  },
  ultraFeed: {
    ...commentBodyStyles(theme),
    marginTop: 0,
    marginBottom: 0,
    '& p:first-child': {
      marginTop: '0 !important',
    },
    '& p:last-child': {
      marginBottom: '0 !important',
    },
    // Hide a single leading br in first paragraph, unless it's a br pair
    '& p:first-child:not(:has(> br:first-child + br)) > br:first-child': {
      display: 'none !important',
    },
    // Hide a single trailing br in last paragraph, unless it's a br pair
    '& p:last-child:not(:has(> br + br:last-child)) > br:last-child': {
      display: 'none !important',
    },
    [theme.breakpoints.down('sm')]: {
      '& h1, & h2, & h3, & h4': {
        marginBlockStart: "0 !important",
        fontFamily: theme.palette.fonts.sansSerifStack,
      },
      '& img, & iframe': {
        maxWidth: '100%',
        height: 'auto',
      },
      '& blockquote, & li': {
      }
    },
  },
  ultraFeedPost: {
    marginTop: 0,
    marginBottom: 0,
    '& p:first-child': {
      marginTop: '0 !important',
    },
    '& p:last-child': {
      marginBottom: '0 !important',
    },
    // Hide a single leading br in first paragraph, unless it's a br pair
    '& p:first-child:not(:has(> br:first-child + br)) > br:first-child': {
      display: 'none !important',
    },
    // Hide a single trailing br in last paragraph, unless it's a br pair
    '& p:last-child:not(:has(> br + br:last-child)) > br:last-child': {
      display: 'none !important',
    },
    [theme.breakpoints.down('sm')]: {
      '& h1, & h2, & h3, & h4': {
        marginBlockStart: "0 !important",
      },
      '& img, & iframe': {
        maxWidth: '100%',
        height: 'auto',
      },
      '& blockquote, & li': {
      }
    },
  },
  // Composed from `researchDocumentBodyStyles`, which itself spreads
  // `postBodyStyles` for full coverage (spoilers, footnotes, embeds, code,
  // tables, etc.) and then overrides editor-specific typography under
  // `[data-lexical-editor]`. Combined with `contentStylesClassnames`
  // skipping `base` for this type, that gives the editor exactly one source
  // of post-body styling — its own — instead of stacking post-rendering
  // defaults underneath and fighting them with overrides.
  researchDocumentBody: researchDocumentBodyStyles(theme),
}), { stylePriority: -1 });

export type ContentStyleType = "post"|"postHighlight"|"comment"|"commentExceptPointerEvents"|"answer"|"tag"|"debateResponse"|"llmChat"|"ultraFeed"|"ultraFeedPost"|"researchDocument";

export function contentStylesClassnames(classes: ReturnType<typeof styles.styles>, contentType: ContentStyleType) {
  // The research-document editor opts out of the shared `base` (postBodyStyles)
  // so its self-contained rule set isn't fighting post-rendering defaults like
  // serif typography on `& li`. Every other content type still inherits base.
  const includeBase = contentType !== "researchDocument";
  return classNames(
    includeBase && classes.base, "content",
    contentType==="post" && classes.postBody,
    contentType==="postHighlight" && classes.postHighlight,
    contentType==="comment" && classes.commentBody,
    contentType==="commentExceptPointerEvents" && classes.commentBodyExceptPointerEvents,
    contentType==="answer" && classes.answerBody,
    contentType==="tag" && classes.tagBody,
    contentType==="debateResponse" && classes.debateResponseBody,
    contentType==="llmChat" && classes.llmChat,
    contentType==="ultraFeed" && classes.ultraFeed,
    contentType==="ultraFeedPost" && classes.ultraFeedPost,
    contentType==="researchDocument" && classes.researchDocumentBody,
  );
}
